import { Home, Database, Settings, Users, LifeBuoy, CreditCard, ListChecks, Cpu } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useGetSupportTicketCounts } from '@/api/control/control-plane/control-plane';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from '@/components/ui/sidebar';
import { OrganizationSwitcher } from '@/components/orgs/OrganizationSwitcher';
import { UserMenu } from '@/components/user/UserMenu';
import { useOrgs } from '@/contexts/OrgsContext';
import { useRoles } from '@/contexts/RolesContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMemo } from 'react';
import { fillSupportTemplate } from '@/lib/support';

/**
 * Application sidebar with navigation items
 */
export function AppSidebar() {
  const location = useLocation();
  const { selectedOrganizationId } = useOrgs();
  const { isAdmin, isBillingManager } = useRoles();
  const { t } = useLanguage();
  const { data: countsResponse } = useGetSupportTicketCounts(selectedOrganizationId ?? '', {
    query: { enabled: !!selectedOrganizationId, staleTime: 60 * 1000, retry: false },
  });
  const counts = countsResponse?.data ?? null;
  const needsReply = counts?.needsReply ?? 0;

  const orgPrefix = `/dashboard/orgs/${selectedOrganizationId ?? ''}`;
  const supportUrl = `${orgPrefix}/support`;

  const menuItems = useMemo(() => {
    const items: { title: string; url: string; icon: typeof Home }[] = [
      { title: t.sidebar.dashboard, url: '/dashboard', icon: Home },
      { title: t.sidebar.profiles, url: `${orgPrefix}/profiles`, icon: Database },
      { title: t.sidebar.allRuns, url: `${orgPrefix}/runs`, icon: ListChecks },
    ];

    items.push({ title: t.sidebar.agents, url: `${orgPrefix}/agents`, icon: Cpu });

    if (isAdmin) {
      items.push({ title: t.sidebar.organizationMembers, url: `${orgPrefix}/members`, icon: Users });
    }

    if (isAdmin || isBillingManager) {
      items.push({ title: t.sidebar.billing, url: `${orgPrefix}/billing`, icon: CreditCard });
    }

    // Support is available to every role; the help center link lives on the Support page.
    items.push(
      { title: t.sidebar.settings, url: '/dashboard/settings', icon: Settings },
      { title: t.sidebar.support, url: supportUrl, icon: LifeBuoy },
    );

    return items;
  }, [orgPrefix, supportUrl, isAdmin, isBillingManager, t]);

  return (
    <Sidebar>
      <SidebarHeader className="border-b px-4 py-3">
        <OrganizationSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={location.pathname === item.url}>
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                  {item.url === supportUrl && needsReply > 0 && (
                    <SidebarMenuBadge className="rounded-full background-warning-strong !text-[color:var(--warning-strong-foreground)]">
                      <span aria-hidden>{needsReply}</span>
                      <span className="sr-only">
                        {fillSupportTemplate(
                          needsReply === 1 ? t.support.waitingBanner.titleOne : t.support.waitingBanner.titleOther,
                          { count: needsReply }
                        )}
                      </span>
                    </SidebarMenuBadge>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t p-2">
        <UserMenu />
      </SidebarFooter>
    </Sidebar>
  );
}
